import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/styles'
import { useFormattingContext } from '../../FormattingProvider/FormattingContext'
import SydLabel from '../../../commonDesign/SydLabel'
import ChartProvider from '../../../charts/echarts/ChartProvider'
import PieChart from '../../../charts/echarts/PieChart'
import { sum } from './utils'

export const useStyles = makeStyles(() => ({
  description: {
    padding: '.5rem 0',
    margin: 0,
    fontSize: '.8rem',
    color: 'var(--color-neutral-500)'
  },
  total: {
    padding: '1rem',
    display: 'flex',
    justifyContent: 'space-between',
    border: 'var(--border-neutral-thin)'
  },
  breakdown: {
    padding: '1rem .1rem',
    display: 'flex',
    flexDirection: 'column'
  },
  breakdownItem: {
    display: 'flex',
    padding: '.5rem 1rem',
    justifyContent: 'space-between'
  },
  metric: {
    display: 'flex',
    justifyContent: 'center'
  }
}))

function PLDCard ({
  excludeZeroValues,
  showBreakdown = true,
  title,
  description,
  assets,
  assetTypes,
  chartProps = {},
  seriesProps = {},
  breakdownFormat = 'money'
}) {
  const classes = useStyles()
  const { formatter } = useFormattingContext()

  const sumAllAssets = useMemo(() => sum(assets, a => a.value), [assets])
  const assetTypeSums = useMemo(() => {
    const result = assetTypes.reduce((acc, curr) => {
      const items = assets.filter(a => a.assetType.id === curr.id)

      acc.push({
        assetType: curr,
        assets: items,
        total: sum(items, a => a.value)
      })

      return acc
    }, [])

    if (excludeZeroValues) {
      return result.filter(r => r.total !== 0)
    }

    return result
  }, [assets, assetTypes, excludeZeroValues])

  const chartData = useMemo(() => {
    return assetTypeSums.map(s => {
      const result = {
        value: s.total,
        name: s.assetType.description
      }

      if ('color' in s.assetType) {
        result.itemStyle = {
          color: s.assetType.color
        }
      }

      return result
    })
  }, [assetTypeSums])

  const assetBreakdown = useMemo(() => {
    return assetTypeSums.sort((a, b) => b.total - a.total).map(s => {
      return {
        ...s.assetType,
        total: formatter(s.total, breakdownFormat),
        label: {
          show: true
        }
      }
    })
  }, [assetTypeSums, formatter, breakdownFormat])

  const seriesConfig = useMemo(() => {
    return {
      dimension: assetTypes.map(({ name, description, type = 'string' }) => ({ type, name, displayString: description })),
      radius: '50%',
      ...(seriesProps ?? {})
    }
  }, [assetTypes, seriesProps])

  const chartConfig = useMemo(() => {
    return {
      tooltip: {
        trigger: 'item'
      },
      height: 500,
      width: 500,
      ...chartProps
    }
  }, [chartProps])

  return (
    <div className='opal-card-md'>
      <SydLabel label={title} style={{ fontSize: '1.2rem' }} />
      <p className={classes.description}>{description}</p>
      <div className={classes.metric}>
        <ChartProvider {...chartConfig}>
          <PieChart
            seriesId={title}
            name={title}
            data={chartData}
            {...seriesConfig}
          />
        </ChartProvider>
      </div>
      {showBreakdown && (
        <div className={classes.breakdown}>
          <div className={classes.total}>
            <span>Total</span>
            <span>{formatter(sumAllAssets, breakdownFormat)}</span>
          </div>
          {assetBreakdown.map(assetData => (
            <div key={assetData.id} className={classes.breakdownItem}>
              <span>{assetData.description}</span>
              <span>{assetData.total}</span>
            </div>
          ))}
        </div>
      )}

    </div>
  )
}

PLDCard.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  assets: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    assetType: PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      color: PropTypes.string
    }).isRequired
  })).isRequired,
  assetTypes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    color: PropTypes.string
  })).isRequired,
  excludeZeroValues: PropTypes.bool,
  seriesProps: PropTypes.object,
  showBreakdown: PropTypes.bool,
  chartProps: PropTypes.object,
  breakdownFormat: PropTypes.string
}

export default PLDCard
